export type UserRole = "ADMIN" | "MODERATOR" | "MODERATOR_FREE" | "SAFEE_MODERATOR" | "SAFEE_STAFF" | "GUEST" | "USER" | "VIGILANE";

// Define resource permissions based on roles
export const ROLE_PERMISSIONS = {
  // Dashboard - Tous les rôles sauf USER
  dashboard: ["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SAFEE_MODERATOR", "GUEST", "VIGILANE"] as UserRole[],

  // Alertes - ADMIN, SAFEE_STAFF et MODERATOR (avec limitations)
  "alert-history": ["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "VIGILANE"] as UserRole[],

  // Monitoring - ADMIN, SAFEE_STAFF, MODERATOR, SAFEE_MODERATOR et VIGILANE (avec limitations)
  monitoring: ["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SAFEE_MODERATOR", "VIGILANE"] as UserRole[],

  // Utilisateurs - ADMIN, SAFEE_STAFF, MODERATOR et GUEST (lecture seule)
  users: ["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "GUEST", "VIGILANE"] as UserRole[],

  // Événements - ADMIN, SAFEE_STAFF et SAFEE_MODERATOR
  "safe-events": ["ADMIN", "SAFEE_STAFF", "SAFEE_MODERATOR"] as UserRole[],

  // Codes Promos - ADMIN uniquement (SAFEE_STAFF exclu)
  "promo-codes": ["ADMIN"] as UserRole[],

  // Premium Devices - ADMIN et SAFEE_STAFF (bulk operations ADMIN only côté backend)
  "premium-devices": ["ADMIN", "SAFEE_STAFF"] as UserRole[],

  // Discounts - ADMIN uniquement (SAFEE_STAFF exclu)
  discounts: ["ADMIN"] as UserRole[],

  // Pricing - Tous les rôles incluant SAFEE_STAFF
  pricing: ["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SAFEE_MODERATOR", "GUEST"] as UserRole[],

  // Entreprises - ADMIN, SAFEE_STAFF, MODERATOR et GUEST (avec limitations)
  company: ["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "GUEST"] as UserRole[],

  // OTA - ADMIN uniquement (SAFEE_STAFF exclu)
  ota: ["ADMIN"] as UserRole[],

  // Localisation - ADMIN, SAFEE_STAFF et MODERATOR (avec limitations)
  location: ["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE"] as UserRole[],

  // Journeys - ADMIN, SAFEE_STAFF et MODERATOR (avec limitations)
  journeys: ["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE"] as UserRole[],

  // Shared Places - ADMIN, SAFEE_STAFF, MODERATOR, MODERATOR_FREE et GUEST
  places: ["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "GUEST"] as UserRole[],

  // Profile - Accessible à tous les rôles connectés incluant SAFEE_STAFF
  profile: ["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "SAFEE_MODERATOR", "GUEST"] as UserRole[],
};

  // Resources accessible to ADMIN, MODERATOR and SAFEE_MODERATOR
//   SHARED: {
//     resources: ["dashboard"] as const,
//     allowedRoles: ["ADMIN", "MODERATOR", "SAFEE_MODERATOR"] as UserRole[]
//   },

//   // Resources accessible to ADMIN and MODERATOR only
//   ADMIN_MODERATOR_ONLY: {
//     resources: ["location", "users"] as const,
//     allowedRoles: ["ADMIN", "MODERATOR"] as UserRole[]
//   },

//   // Resources accessible only to ADMIN
//   ADMIN_ONLY: {
//     resources: ["alert-history", "ota", "promo-codes", "company"] as const,
//     allowedRoles: ["ADMIN"] as UserRole[]
//   },

//   // Resources accessible only to SAFEE_MODERATOR
//   SAFEE_MODERATOR_ONLY: {
//     resources: ["monitoring", "safe-events"] as const,
//     allowedRoles: ["ADMIN", "SAFEE_MODERATOR"] as UserRole[]
//   }
// } as const;

/**
 * Check if a user role has access to a specific resource
 */
export const hasResourceAccess = (userRole: UserRole, resourceName: string): boolean => {
  // USER role has no access to backoffice
  if (userRole === "USER") {
    return false;
  }

  const allowedRoles = ROLE_PERMISSIONS[resourceName as keyof typeof ROLE_PERMISSIONS];
  if (!allowedRoles) {
    return false;
  }

  return allowedRoles.includes(userRole);
};

/**
 * Get allowed roles for a specific resource
 */
export const getAllowedRolesForResource = (resourceName: string): UserRole[] => {
  return ROLE_PERMISSIONS[resourceName as keyof typeof ROLE_PERMISSIONS] || [];
};

/**
 * Filter resources based on user role for navigation menu
 */
export const filterResourcesByRole = (
  resources: Array<{ name: string; [key: string]: any }>,
  userRole: UserRole
): Array<{ name: string; [key: string]: any }> => {
  return resources.filter(resource => hasResourceAccess(userRole, resource.name));
};

/**
 * Check if current user can access another user's data based on company membership
 */
export const canAccessUserData = (
  currentUser: { id: number; role: string; entrepriseId?: number },
  targetUser: { id: number; entrepriseId?: number; hasVigilane?: boolean }
): boolean => {
  // ADMIN and SAFEE_STAFF users can access any user data (global scope)
  if (currentUser.role === 'ADMIN' || currentUser.role === 'SAFEE_STAFF') {
    return true;
  }

  // VIGILANE users can access any user with hasVigilane: true
  if (currentUser.role === 'VIGILANE') {
    return targetUser.hasVigilane === true;
  }

  // SAFEE_MODERATOR cannot access user data
  if (currentUser.role === 'SAFEE_MODERATOR') {
    return false;
  }

  // Users can always access their own data
  if (currentUser.id === targetUser.id) {
    return true;
  }

  // MODERATOR and MODERATOR_FREE users restrictions
  if (currentUser.role === 'MODERATOR' || currentUser.role === 'MODERATOR_FREE') {
    // Si le MODERATOR/MODERATOR_FREE a une entreprise, il ne peut voir que les utilisateurs de la même entreprise
    if (currentUser.entrepriseId) {
      return currentUser.entrepriseId === targetUser.entrepriseId;
    }

    // Si le MODERATOR/MODERATOR_FREE n'a pas d'entreprise, laisser le backend vérifier les securityContacts
    // Le frontend permet l'accès et le backend déterminera s'il y a des securityContacts communs
    return true;
  }

  // GUEST users can only read their company's users
  if (currentUser.role === 'GUEST') {
    return currentUser.entrepriseId === targetUser.entrepriseId;
  }

  // For other roles, check if users belong to the same company
  if (currentUser.entrepriseId && targetUser.entrepriseId) {
    return currentUser.entrepriseId === targetUser.entrepriseId;
  }

  // If company information is missing, deny access by default
  return false;
};

/**
 * Check if user can create/update/delete in a resource
 */
export const canModifyResource = (userRole: UserRole, resourceName: string): boolean => {
  // USER role cannot modify anything
  if (userRole === "USER") {
    return false;
  }

  // VIGILANE role is read-only for all resources except users (can add)
  if (userRole === "VIGILANE") {
    if (resourceName === "users") {
      return true; // Can add users to Vigilane
    }
    return false; // Read-only for other resources
  }

  // GUEST role can only create company during onboarding, read-only for users
  if (userRole === "GUEST") {
    if (resourceName === "company") {
      return true; // Can create company during onboarding
    }
    return false; // Read-only for other resources
  }

  // Check if user has access to the resource first
  if (!hasResourceAccess(userRole, resourceName)) {
    return false;
  }

  // Special cases for limited access
  switch (resourceName) {
    case "alert-history":
      // ADMIN and SAFEE_STAFF can modify, MODERATOR and MODERATOR_FREE can only view (read-only)
      return userRole === "ADMIN" || userRole === "SAFEE_STAFF";

    case "monitoring":
      // ADMIN, SAFEE_STAFF, MODERATOR and MODERATOR_FREE can modify, SAFEE_MODERATOR read-only
      return userRole === "ADMIN" || userRole === "SAFEE_STAFF" || userRole === "MODERATOR" || userRole === "MODERATOR_FREE";

    case "users":
      // ADMIN, SAFEE_STAFF, MODERATOR and MODERATOR_FREE can manage users
      // Note: delete is handled separately in UI (SAFEE_STAFF cannot delete)
      return userRole === "ADMIN" || userRole === "SAFEE_STAFF" || userRole === "MODERATOR" || userRole === "MODERATOR_FREE";

    case "safe-events":
      // ADMIN, SAFEE_STAFF and SAFEE_MODERATOR can manage events
      // Note: delete is handled separately in UI (SAFEE_STAFF cannot delete)
      return userRole === "ADMIN" || userRole === "SAFEE_STAFF" || userRole === "SAFEE_MODERATOR";

    case "company":
      // ADMIN, SAFEE_STAFF, MODERATOR and MODERATOR_FREE can edit companies
      return userRole === "ADMIN" || userRole === "SAFEE_STAFF" || userRole === "MODERATOR" || userRole === "MODERATOR_FREE";

    case "premium-devices":
      // ADMIN and SAFEE_STAFF can modify single devices
      // Note: bulk operations are hidden for SAFEE_STAFF in UI
      return userRole === "ADMIN" || userRole === "SAFEE_STAFF";

    case "location":
      // Read-only for all roles
      return false;

    case "places":
      // ADMIN, SAFEE_STAFF, MODERATOR, MODERATOR_FREE and GUEST can modify shared places
      // GUEST can only create/edit places for their company
      return ["ADMIN", "SAFEE_STAFF", "MODERATOR", "MODERATOR_FREE", "GUEST"].includes(userRole);

    default:
      // For other resources, ADMIN and SAFEE_STAFF have full access
      return userRole === "ADMIN" || userRole === "SAFEE_STAFF";
  }
};

/**
 * Get data scope for a user based on their role
 */
export const getDataScope = (user: { role: string; entrepriseId?: number; id: number }) => {
  switch (user.role) {
    case "ADMIN":
    case "SAFEE_STAFF":
      return { scope: "ALL", filter: {} };

    case "VIGILANE":
      return { scope: "VIGILANE_USERS", filter: { vigilaneId: { not: null } } };

    case "MODERATOR":
    case "MODERATOR_FREE":
      if (user.entrepriseId) {
        return { scope: "COMPANY", filter: { entrepriseId: user.entrepriseId } };
      }
      return { scope: "SECURITY_CONTACTS", filter: { userId: user.id } };

    case "SAFEE_MODERATOR":
      return { scope: "EVENTS", filter: { createdById: user.id } };

    case "GUEST":
      return { scope: "COMPANY_READONLY", filter: { entrepriseId: user.entrepriseId } };

    default:
      return { scope: "NONE", filter: {} };
  }
};
